import type { ReactElement } from 'react'

import { ICON_PATHS, type IconName, type IconPart } from './icon-paths.ts'

interface IconBaseProps {
  readonly name: IconName
  /** Rendered size in CSS pixels. The 24-unit viewBox scales to it. */
  readonly size?: number
  readonly className?: string
}

/**
 * Two accessibility modes, and the type system forces a choice.
 *
 * A decorative icon sits beside a text label and must be silent; a meaningful one
 * carries information and must be named. Modelling this as a discriminated union
 * means a nameless meaningful icon does not compile. Every icon in this design is
 * decorative — each one sits next to its own words — but the primitive must not
 * make the unsafe case the easy one.
 */
export type IconProps = IconBaseProps &
  (
    | { readonly decorative: true; readonly label?: never }
    | { readonly decorative?: false; readonly label: string }
  )

const DEFAULT_SIZE = 20
const VIEW_BOX = '0 0 24 24'
const STROKE_WIDTH = 1.6

function renderPart(part: IconPart, key: string): ReactElement {
  if (part.tag === 'circle') {
    return <circle key={key} cx={part.cx} cy={part.cy} r={part.r} />
  }

  if (part.tag === 'rect') {
    return (
      <rect key={key} x={part.x} y={part.y} width={part.width} height={part.height} rx={part.rx} />
    )
  }

  return <path key={key} d={part.d} />
}

/**
 * Pure presenter over the frozen geometry record.
 *
 * Colour comes from `currentColor` plus a class, never from a `style` attribute
 * (blocked by `style-src 'self'`) and never from `var()` inside a presentation
 * attribute — support for `var()` there is uneven across engines, while
 * `currentColor` is universal.
 */
export function Icon({
  name,
  size = DEFAULT_SIZE,
  className,
  decorative,
  label,
}: IconProps): ReactElement {
  const spec = ICON_PATHS[name]
  const filled = spec.variant === 'fill'

  const naming =
    decorative === true
      ? ({ 'aria-hidden': true, focusable: false } as const)
      : ({ role: 'img', 'aria-label': label, focusable: false } as const)

  return (
    <svg
      className={className === undefined ? 'icon' : `icon ${className}`}
      width={size}
      height={size}
      viewBox={VIEW_BOX}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? undefined : 'currentColor'}
      strokeWidth={filled ? undefined : STROKE_WIDTH}
      strokeLinecap={filled ? undefined : 'round'}
      strokeLinejoin={filled ? undefined : 'round'}
      {...naming}
    >
      {spec.parts.map((part, index) => renderPart(part, `${name}-${String(index)}`))}
    </svg>
  )
}
